import type { QueryScenariosRequest } from '@/lib/types/rome';
import type { DemoModeFilters } from '@/providers/DemoModeProvider';

/** The subset of Rome query params that demo mode contributes. */
export type DemoQueryParams = Pick<
  QueryScenariosRequest,
  | 'tags'
  | 'exclude_tags'
  | 'tag_groups'
  | 'name_term_groups'
  | 'exclude_search_terms'
  | 'user_ids'
  | 'exclude_user_ids'
  | 'exclude_scenario_names'
>;

/**
 * Translate demo-mode filters into Rome query params.
 *
 * Nothing is forced: the include tag/name chips (which default to ``is_demo``
 * but are fully removable) become OR-of-AND groups (``tag_groups`` /
 * ``name_term_groups`` -- separate chips OR, ``&`` within a chip ANDs). Any
 * caller ``baseTags`` (e.g. the scenario explorer's own tag filter) are ANDed
 * via ``tags``. Authors map to ``user_ids`` (include, OR) / ``exclude_user_ids``,
 * and hidden scenarios to exact ``exclude_scenario_names``. Hidden suites and
 * suite-runs fold into ``exclude_tags`` (their tags are exact tag exclusions).
 *
 * @param demo - The active demo-mode filters.
 * @param baseTags - Caller-supplied tags to AND (e.g. the scenario explorer's
 *   own tag filter). Deduplicated; omitted when empty.
 * @returns The demo-derived query params to merge into a request.
 */
export function demoFiltersToQueryParams(demo: DemoModeFilters, baseTags: string[] = []): DemoQueryParams {
  const params: DemoQueryParams = {};
  const tags = [...new Set(baseTags)];
  if (tags.length > 0) params.tags = tags;
  if (demo.tags.length > 0) params.tag_groups = demo.tags;
  if (demo.searchTerms.length > 0) params.name_term_groups = demo.searchTerms;
  // Hidden suites/suite-runs are tag exclusions (their suite_tag / suiteRunTag
  // are the actual ES tags), so they fold into exclude_tags alongside the
  // user's manually excluded tags.
  const excludeTags = [...new Set([...demo.excludeTags, ...demo.hiddenSuites, ...demo.hiddenSuiteRuns])];
  if (excludeTags.length > 0) params.exclude_tags = excludeTags;
  if (demo.excludeSearchTerms.length > 0) params.exclude_search_terms = demo.excludeSearchTerms;
  if (demo.userIds.length > 0) params.user_ids = demo.userIds;
  if (demo.excludeUserIds.length > 0) params.exclude_user_ids = demo.excludeUserIds;
  if (demo.hiddenScenarios.length > 0) params.exclude_scenario_names = demo.hiddenScenarios;
  return params;
}
